/**
 * @format
 * @module lib/commercial/dfp-ads/adslot-size-mapping
 * @description A global store of all the possible adslot size mappings.
 * Mostly depending on the page category and viewport tier.
 */
import tgamConfig from "config/tgam";
import { isAnonymous } from "lib/identity/api";
import find from "lodash/find";
import { getBreakpointName } from "utils/detect";
import logger from "utils/logger";
import { getProp } from "utils/utils";
import sizeMapsForCategories from "./size-mapping";

const debugTag = "[DFP-ADS]";

// Ad sizes "generally seen as standard in the industry" (used for header bidding)
// It's easier to compare the array values when they're converted into strings
const standardAdSizes = [
  [160, 600],
  [300, 250],
  [300, 600],
  [300, 1050],
  [320, 50],
  [728, 90],
  [970, 90],
  [970, 66],
  [970, 250]
].map(size => JSON.stringify(size));

/**
 * Filter out non-standard sizemaps
 * @param {Array} sizemap - An ad slot's sizemap array
 * @returns {Array} A filtered copy of the sizemap array
 */
export function getStandardAdSizes(sizemap) {
  return sizemap.filter(sm => {
    return standardAdSizes.includes(JSON.stringify(sm));
  });
}

/**
 * Constant enum for different breakpoint minimum widths
 * @readonly
 * @enum {Number}
 */
export const WIDTHS = {
  mobile: 0,
  portrait: getBreakpointName("tablet").px,
  landscape: getBreakpointName("tabletMedium").px,
  desktop: getBreakpointName("desktop").px
};

/**
 * @method getPresetKey
 * @description Map the unitId value into a value without "gpt" and #1
 * @param {String} unitId - that is a unique adslot ID value for the current page
 * @returns {String} e.g. `flex`, `flex2`, `flex3`
 */
export function getPresetKey(unitId = "") {
  let ret = null; // default
  if (unitId) {
    const slot = unitId.split("-"); // format: `type-gpt-#`, `ldbd-gpt-1`, `halfpager-gpt-3`
    if (slot.length === 3) {
      ret = slot[0] + (parseInt(slot[2]) > 1 ? slot[2] : "");
    }
  }
  return ret;
}

/**
 * @method mapSizeMapping
 * @description Generate the common sizeMapping array object, based on the params
 * @param {Object} preset - of the sizeMapping of the entire category
 * @param {Object} [width] - of browser breakpoint minimum widths
 * @param {String} [key] - of the slot type
 * @param {Boolean} [cloned] - is this an infinitely-repeating dynamic ad?
 * @returns {Array} sizeMapping of the specific slot type
 */
export function mapSizeMapping(preset, width = WIDTHS, key = "all", cloned = false) {
  function getSizes(breakpoint) {
    return (
      preset[breakpoint].all ||
      preset[breakpoint][key] ||
      (cloned ? preset[breakpoint].flexN || [] : [])
    );
  }
  return [
    {
      minWidth: width.desktop,
      sizes: getSizes("desktop")
    },
    {
      minWidth: width.landscape,
      sizes: getSizes("landscape")
    },
    {
      minWidth: width.portrait,
      sizes: getSizes("portrait")
    },
    {
      minWidth: width.mobile,
      sizes: getSizes("mobile")
    }
  ];
}

/**
 * @method getPageCategory
 * @description Determine the slot size category type
 * @param {Object} meta - of page meta data
 * @returns {String} of category
 */
export function getPageCategory(meta) {
  const isMatch = props => props.every(p => meta[p]);
  const isLayout = n => meta.pbLayout == n;
  const hasKeyword = word => meta.keywords.indexOf(word) !== -1;
  const isFinancial = name => /markets\/(portfolio|watchlist)/.test(name);
  const isAnonSuperheroContext = isAnonSuperhero(); // is anonymous user and section should get superhero ads?

  // Order matters here. First category item to evaluate to `true` is returned.
  const cats = {
    homePage: () => isMatch(["isHomePbLayout"]), // Page that uses PB "homepage" layout
    videoSponsoredKeyword: () =>
      isMatch(["isVideoPage", "advContent"]) && hasKeyword("adv-sponsored"), // Sponsored video page (with special meta keyword)
    videoSponsored: () => isMatch(["isVideoPage", "advContent"]), // Sponsored video page (without special meta keyword)
    video: () => isMatch(["isVideoPage"]), // Non-sponsored (regular) video page
    anonLongReadArticle: () => isAnonSuperheroContext && isMatch(["isStoryLongReadPage"]), // Anonymous long read article page
    longReadArticle: () => isMatch(["isStoryLongReadPage"]), // Long read article page
    anonPremiumArticle: () => isAnonSuperheroContext && isMatch(["isStoryPremiumPage"]), // Anonymous premium article page
    premiumArticle: () => isMatch(["isStoryPremiumPage"]), // Premium article page
    advInteractiveArticle: () => isMatch(["isStoryInteractivePage", "advContent"]), // ADV interactive article page
    interactiveArticle: () => isMatch(["isStoryInteractivePage"]), // Interactive article page
    advStandardArticle: () => isMatch(["isStoryPage", "advContent"]), // ADV content article
    anonStandardArticle: () => isAnonSuperheroContext && isMatch(["isStoryPage"]), // Anonymous standard article page
    standardArticle: () => isMatch(["isStoryPage"]), // Standard article page
    financialPageWatchlist: () => isMatch(["isFinancialSection"]) && isFinancial(meta.siteName), // Financial Watchlist section page
    financialPageFullChart: () =>
      isMatch(["isFinancialPage"]) && getProp(meta, "JSbundleName", "") === "financial-full-chart", // Financial full-chart page
    financialPage: () => isMatch(["isFinancialPage"]), // Financial section or leaf page
    noAds: () => isMatch(["isSectionPage"]) && meta.pagesubtype === "preferences", // Your Globe page
    electionResultsPage: () =>
      isMatch(["isSectionPage"]) && isLayout("static") && meta.pagesubtype === "election", // Election Results page
    premiumSectionInvestingTest: () =>
      isMatch(["isSectionPage", "isPremiumPbLayout"]) &&
      meta.siteName === "investing" &&
      meta.testVariant === "inviewtest", // Premium Investing section "test" page
    premiumSectionInvesting: () =>
      isMatch(["isSectionPage", "isPremiumPbLayout"]) && meta.siteName === "investing", // Premium Investing section page
    anonPremiumSection: () =>
      isAnonSuperheroContext && isMatch(["isSectionPage", "isPremiumPbLayout"]), // Anonymous premium section page
    premiumSection: () => isMatch(["isSectionPage", "isPremiumPbLayout"]), // Premium section page
    anonStandardSection: () => isAnonSuperheroContext && isMatch(["isSectionPage"]), // Anonymous standard section page
    standardSection: () => isMatch(["isSectionPage"]), // Standard section page
    static: () => isMatch(["isStatic"]) && isLayout("static"), // Static page - no right-rail
    static2: () => isMatch(["isStatic"]) && isLayout("static-2") // Static page - yes right-rail
  };
  return find(Object.keys(cats), key => cats[key]()) || "standardSection";
}

/**
 * Determine if the user is anonymous, and the section is marked for superhero ads.
 * ARC-6025: Add SuperHero ad sizes to select sections where Hero ad sizes exist for anonymous readers
 * @returns {Boolean}
 */
export function isAnonSuperhero() {
  const anonSections = getProp(tgamConfig, "env.adOpsSuperheroSections", "").split(",");
  const urlRelative = getProp(tgamConfig, "meta.urlRelative", "");
  const isAnonSection = anonSections.filter(path => urlRelative.includes(path)).length > 0;
  const isAnonUser = isAnonymous();

  logger.info(`${debugTag} isAnonSuperhero:`, {
    anonSections,
    urlRelative,
    isAnonSection,
    isAnonUser
  });

  return isAnonSection && isAnonUser;
}

/**
 * @method getViewportTier
 * @description Determine the viewport tier that is usable for ad slot logic
 * @param {Object} [width=WIDTH] - of browser breakpoint minimum widths
 * @returns {String} of the viewport tier
 */
export function getViewportTier(width = WIDTHS) {
  const isMatch = bp => window.matchMedia("(min-width: " + bp + "px)").matches;
  const tiers = {
    desktop: () => isMatch(width.desktop),
    landscape: () => isMatch(width.landscape),
    portrait: () => isMatch(width.portrait)
  };
  return find(Object.keys(tiers), key => tiers[key]()) || "mobile";
}

/**
 * @method getViewportBreakpoint
 * @description Determine the viewport breakpoint that is usable for ad slot logic
 * @param {Object} [width=WIDTH] - of browser breakpoint minimum widths
 * @returns {Number} of the viewport breakpoint
 */
export function getViewportBreakpoint(width = WIDTHS) {
  return width[getViewportTier(width)];
}

/**
 * @method filterPageAdverts
 * @description Filter out all page adslot units that do not have a size mapping
 * @param {Array} pgUnits - of ad slot units
 * @param {Object} meta - metadata for page
 * @param {Object} [width] - of browser breakpoint minimum widths
 * @param {Object} [sizes] - of category sizes to search through
 * @returns {Array} of filtered ad slot units
 */
export function filterPageAdverts(pgUnits, meta, width = WIDTHS, sizes = sizeMapsForCategories) {
  const tier = getViewportTier(width);
  const cat = getPageCategory(meta);
  logger.info(`${debugTag} page category:`, cat);
  logger.info(`${debugTag} viewport tier:`, tier);

  return pgUnits.filter(ad => {
    const key = getPresetKey(ad.id);
    let prop = getProp(sizes, [cat, tier, key]) || getProp(sizes, [cat, tier, "all"]) || null;

    // If this is an infinitely-repeating dynamic ad, check
    // to see if the category/tier contains a "flexN" key
    if (!prop && ad.classList.contains("js-f-ad-cloned")) {
      prop = getProp(sizes, [cat, tier, "flexN"]);
    }
    return Array.isArray(prop) ? prop.length > 0 : false;
  });
}

/**
 * @method buildCommonMapping
 * @description Determines which responsive ad sizes may be returned by DFP for
 * a given ad type. Returns a basic array, which is all that's required for
 * Prebid. This array will later be used as an input for "gptSizeMapping()"
 * (once GPT is ready), which will yield a "googletag.SizeMappingArray".
 * @param {String} unitId - the #id of the containing element for this ad
 * @param {Object} meta - metadata for page
 * @param {Boolean} cloned - is this an infinitely-repeating dynamic ad?
 * @param {Object} [width] - of browser breakpoint minimum widths
 * @param {Object} [sizes] - of category sizes to search through
 * @returns {Object} of googletag.SizeMappingArray - see GPT docs
 */
export function buildCommonMapping(
  unitId,
  meta,
  cloned = false,
  width = WIDTHS,
  sizes = sizeMapsForCategories
) {
  const key = getPresetKey(unitId);
  const cat = getPageCategory(meta);
  return mapSizeMapping(sizes[cat], width, key, cloned);
}

/**
 * @method buildCustomMapping
 * @description Create a custom ad slot size mapping for a unique ad. Returns
 * a basic array, which is all that's required for Prebid. This array will
 * later be used as an input for "gptSizeMapping()" (once GPT is ready), which
 * will yield a "googletag.SizeMappingArray".
 * @param {Object} sizemap - of the sizeMapping of the entire category
 * @param {Object} [width] - of browser breakpoint minimum widths
 * @param {String} [key] - of the slot type
 * @returns {Object} of googletag.SizeMappingArray - see GPT docs
 */
export function buildCustomMapping(sizemap, width, key) {
  return mapSizeMapping(sizemap, width, key);
}

/**
 * @method buildRefreshMapping
 * @description Create a simple refresh ad slot size mapping. Returns a basic
 * array, which is all that's required for Prebid. This array will later be
 * used as an input for "gptSizeMapping()" (once GPT is ready), which will
 * yield a "googletag.SizeMappingArray".
 * @param {Object} sizes - of the sizeMapping of the entire category
 * @returns {Object} of googletag.SizeMappingArray - see GPT docs
 */
export function buildRefreshMapping(sizes) {
  return [
    {
      minWidth: 0,
      sizes: sizes
    }
  ];
}
